import { useState, useCallback, useRef, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Typography,
  Button,
  IconButton,
  Tooltip,
  Grid,
  Chip,
  CircularProgress,
  Alert,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import EditIcon from '@mui/icons-material/Edit';
import SendIcon from '@mui/icons-material/Send';
import PrintIcon from '@mui/icons-material/Print';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import SmartphoneIcon from '@mui/icons-material/Smartphone';
import {
  InspectionCategoryComponent,
  InspectionSummary,
  generateInspectionReport,
  SendReportDialog,
  AddRecommendationDialog,
} from '@/components/inspection';
import { InspectionReportDeliveries } from '@/components/inspection/InspectionReportDeliveries';
import { ConfirmDialog } from '@/components/common/ConfirmDialog';
import { useAuthStore } from '@/stores/authStore';
import { useNotify } from '@/stores/notificationStore';
import {
  useInspection,
  useAddPhoto,
  useDeletePhoto,
  useUpdateItemCondition,
  useCompleteInspection,
  useAddRecommendation,
  useUpdateRecommendation,
  useDeleteRecommendation,
  useLinkRecommendationToWorkOrder,
  useUnlinkRecommendationFromWorkOrder,
  useGenerateReport,
  useSendReport,
  type InspectionItem as ApiInspectionItem,
  type InspectionRecommendation,
  type InspectionDetail,
} from '@/hooks/useInspections';
import { useCreateInspectionOffer, type InspectionOfferFinding } from '@/hooks/useInspectionOffers';
import { useWorkshopInfo } from '@/hooks/useTenantSettings';
import { useWorkOrder, type WorkOrderItem } from '@/hooks/useWorkOrders';
import { describeApiError } from '@/lib/describeApiError';
import type { Inspection, InspectionStatus, InspectionCategory, InspectionItemCondition, MeasurementValueEntry } from '@/types';

// Group flat inspection items into category structure expected by UI components
function groupItemsIntoCategories(data: InspectionDetail): InspectionCategory[] {
  const categoryMap = new Map<string, InspectionCategory>();
  for (const item of data.items) {
    if (!categoryMap.has(item.categoryId)) {
      categoryMap.set(item.categoryId, {
        id: item.categoryId, name: item.categoryName, order: categoryMap.size, items: [],
      });
    }
    const recommendation = data.recommendations.find((rec) => rec.inspectionItemId === item.id);
    categoryMap.get(item.categoryId)!.items.push({
      id: item.id,
      categoryId: item.categoryId,
      name: item.itemName,
      description: item.itemDescription ?? undefined,
      condition: item.condition,
      notes: item.notes ?? undefined,
      photos: data.photos.filter((photo) => photo.inspectionItemId === item.id).map((photo) => ({
        id: photo.id, url: photo.fileUrl, thumbnailUrl: photo.thumbnailUrl ?? photo.fileUrl,
        caption: photo.caption ?? undefined, mediaType: photo.fileType, createdAt: photo.createdAt,
      })),
      recommendation: recommendation ? {
        id: recommendation.id, description: recommendation.description, urgency: recommendation.urgency,
        estimatedPriceCents: recommendation.totalCostCents, workOrderItemId: recommendation.workOrderItemId,
        approved: recommendation.approvalStatus === 'approved' || recommendation.approvalStatus === 'auto_approved'
          ? true : recommendation.approvalStatus === 'declined' ? false : undefined,
        approvedAt: recommendation.approvedAt ?? undefined,
      } : undefined,
      order: item.sortOrder,
      hasMeasurement: item.measurementUnit != null,
      measurementValue: item.measurementValue != null ? Number(item.measurementValue) : null,
      measurementUnit: item.measurementUnit ?? undefined,
      measurementValues: item.measurementValues,
      measurementsConfig: item.measurementsConfig,
      subItemsConfig: item.subItemsConfig,
    });
  }
  return Array.from(categoryMap.values());
}

function toOfferFinding(rec: InspectionRecommendation): InspectionOfferFinding {
  return {
    id: rec.id,
    description: rec.description,
    urgency: rec.urgency,
    estimatedPriceCents: rec.totalCostCents,
    workOrderItemId: rec.workOrderItemId,
    offerId: rec.offerId ?? null,
  };
}

export default function InspectionDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation('inspection');
  const { t: tc } = useTranslation('common');
  const { t: tWorkorders } = useTranslation('workorders');
  const notify = useNotify();
  const workshopInfo = useWorkshopInfo();
  const hasPermission = useAuthStore((state) => state.hasPermission);

  const statusConfig: Record<InspectionStatus, { label: string; color: 'default' | 'primary' | 'success' | 'warning' | 'error' | 'info' }> = {
    draft: { label: t('status.draft'), color: 'default' },
    pending: { label: t('status.pending'), color: 'default' },
    in_progress: { label: t('status.in_progress'), color: 'warning' },
    completed: { label: t('status.completed'), color: 'success' },
    sent_to_customer: { label: t('status.sent_to_customer'), color: 'info' },
    approved: { label: t('status.approved'), color: 'success' },
    rejected: { label: t('status.rejected'), color: 'error' },
  };

  // Fetch inspection from API
  const { data: inspectionData, isLoading, error, refetch } = useInspection(id);

  // Mutations
  const addPhoto = useAddPhoto();
  const deletePhoto = useDeletePhoto();
  const updateItemCondition = useUpdateItemCondition();
  const completeInspection = useCompleteInspection();
  const addRecommendation = useAddRecommendation();
  const updateRecommendation = useUpdateRecommendation();
  const deleteRecommendation = useDeleteRecommendation();
  const linkRecommendation = useLinkRecommendationToWorkOrder();
  const unlinkRecommendation = useUnlinkRecommendationFromWorkOrder();
  const createInspectionOffer = useCreateInspectionOffer();
  const generateReport = useGenerateReport();
  const sendReport = useSendReport();

  // Fetch work order items for linking recommendations
  const { data: workOrderData } = useWorkOrder(inspectionData?.bookingId);

  // State for send report dialog
  const [sendDialogOpen, setSendDialogOpen] = useState(false);
  const [currentReportId, setCurrentReportId] = useState<string | null>(null);

  const [isEditing, setIsEditing] = useState(false);
  const [editingRecommendationId, setEditingRecommendationId] = useState<string | null>(null);
  const [deletingRecommendationId, setDeletingRecommendationId] = useState<string | null>(null);
  const canUpdate = hasPermission('inspections.update');
  const canEdit = canUpdate && (inspectionData?.status === 'draft' || inspectionData?.status === 'in_progress');
  const editable = isEditing && canEdit;
  const canOffer = hasPermission('work_orders.update') && !!inspectionData?.bookingId;
  const latestInspection = useRef(inspectionData);
  useEffect(() => { latestInspection.current = inspectionData; }, [inspectionData]);
  const itemSaves = useRef(new Map<string, Promise<void>>());
  const failedItemSaves = useRef(new Set<string>());
  const savedConditions = useRef(new Map<string, ApiInspectionItem['condition']>());

  // The item API requires condition even for notes/measurements. Serialize each
  // item's writes so a note blur cannot undo a condition saved just before it.
  const saveItem = (itemId: string, changes: {
    condition?: ApiInspectionItem['condition']; notes?: string;
    measurementValue?: number | null; measurementValues?: MeasurementValueEntry[];
  }): Promise<void> => {
    if (!id || !editable) return Promise.reject(new Error(t('detail.saveFailed')));
    const item = latestInspection.current?.items.find((entry) => entry.id === itemId);
    if (!item) return Promise.reject(new Error(t('detail.saveFailed')));
    const previous = itemSaves.current.get(itemId) ?? Promise.resolve();
    const changeKey = `${itemId}:${Object.keys(changes).join(',')}`;
    const next = previous.catch(() => undefined).then(async () => {
      const currentItem = latestInspection.current?.items.find((entry) => entry.id === itemId);
      if (!currentItem || !['draft', 'in_progress'].includes(latestInspection.current?.status ?? '')) {
        throw new Error(t('detail.saveFailed'));
      }
      const condition = changes.condition !== undefined
        ? changes.condition : savedConditions.current.has(itemId)
          ? savedConditions.current.get(itemId)! : currentItem.condition;
      await updateItemCondition.mutateAsync({ inspectionId: id, itemId, ...changes, condition });
      savedConditions.current.set(itemId, condition);
      failedItemSaves.current.delete(changeKey);
    }).catch((err: unknown) => {
      failedItemSaves.current.add(changeKey);
      throw err;
    });
    const settled = next.finally(() => {
      if (itemSaves.current.get(itemId) === settled) {
        itemSaves.current.delete(itemId);
        savedConditions.current.delete(itemId);
      }
    });
    itemSaves.current.set(itemId, settled);
    return settled;
  };

  const handleConditionChange = (itemId: string, condition: InspectionItemCondition) => {
    void saveItem(itemId, { condition }).then(
      () => notify.success(t('detail.updated'), t('detail.conditionUpdated')),
      () => notify.error(tc('error'), t('detail.saveFailed')),
    );
  };

  const handleNotesChange = async (itemId: string, notes: string) => {
    try {
      await saveItem(itemId, { notes });
      notify.success(t('detail.updated'), t('detail.notesSaved'));
    } catch (err) {
      notify.error(tc('error'), t('detail.notesSaveFailed'));
      throw err;
    }
  };

  const handleMeasurementChange = (itemId: string, value: number | null) => {
    void saveItem(itemId, { measurementValue: value }).then(
      () => notify.success(t('detail.measurementSaved'), t('detail.measurementUpdated')),
      () => notify.error(tc('error'), t('detail.measurementSaveFailed')),
    );
  };

  const handleMeasurementValuesChange = (itemId: string, measurementValues: MeasurementValueEntry[]) => {
    void saveItem(itemId, { measurementValues }).catch(() => {
      notify.error(tc('error'), t('detail.measurementSaveFailed'));
    });
  };

  const editableRecommendation = (recId: string) => {
    const rec = inspectionData?.recommendations.find((entry) => entry.id === recId);
    return editable && rec && !rec.offerId && !rec.approvalRequestId ? rec : undefined;
  };
  const editingRecommendation = inspectionData?.recommendations.find((rec) => rec.id === editingRecommendationId);
  const editingItem = inspectionData?.items.find((item) => item.id === editingRecommendation?.inspectionItemId);


  // All useCallback hooks must be before conditional returns (React rules of hooks)
  const handleAddPhoto = useCallback(
    async (itemId: string, file: File) => {
      if (!id || !editable) return;

      try {
        await addPhoto.mutateAsync({
          inspectionId: id,
          itemId,
          file,
        });
        notify.success(t('detail.photoAdded'), t('detail.photoSaved'));
      } catch {
        notify.error(tc('error'), t('detail.photoAddFailed'));
      }
    },
    [id, editable, addPhoto, notify, t, tc]
  );

  const handleDeletePhoto = useCallback(
    async (_itemId: string, photoId: string) => {
      if (!id || !editable) return;

      try {
        await deletePhoto.mutateAsync({
          inspectionId: id,
          photoId,
        });
        notify.success(t('detail.photoDeleted'), t('detail.photoDeletedSuccess'));
      } catch {
        notify.error(tc('error'), t('detail.photoDeleteFailed'));
      }
    },
    [id, editable, deletePhoto, notify, t, tc]
  );

  const handleLinkRecommendation = useCallback(
    async (recId: string, workOrderItemId: string) => {
      if (!id || !canUpdate) return;

      await linkRecommendation.mutateAsync({
        inspectionId: id,
        recId,
        workOrderItemId,
      });
      notify.success(t('detail.linked'), t('detail.findingLinkedToWorkOrder'));
    },
    [id, canUpdate, linkRecommendation, notify, t]
  );

  const bookingId = inspectionData?.bookingId;
  const handleAddToOffer = useCallback(
    async (recommendationIds: string[]) => {
      if (!id || !bookingId || !canOffer) return;
      try {
        await createInspectionOffer.mutateAsync({
          inspectionId: id,
          recommendationIds,
        });
        notify.success(t('offer.created'), t('offer.createdDesc'), {
          action: {
            label: t('offer.openWorkOrder'),
            onClick: () => {
              void navigate(`/workorders/${bookingId}`);
            },
          },
        });
      } catch (err) {
        notify.error(tc('error'), describeApiError(err, tc, { fallback: t('offer.createFailed') }));
        throw err;
      }
    },
    [id, bookingId, canOffer, createInspectionOffer, notify, t, tc, navigate],
  );

  const handleCreateAndLinkRecommendation = useCallback(
    async (recId: string, _recommendation: {
      description: string;
      partsCostCents: number;
      laborCostCents: number;
      laborHours?: number;
    }) => {
      await handleAddToOffer([recId]);
    },
    [handleAddToOffer],
  );

  const handleUnlinkRecommendation = useCallback(
    async (recId: string) => {
      if (!id || !canUpdate) return;

      await unlinkRecommendation.mutateAsync({
        inspectionId: id,
        recId,
      });
      notify.success(t('detail.linkRemoved'), t('detail.findingUnlinkedFromWorkOrder'));
    },
    [id, canUpdate, unlinkRecommendation, notify, t]
  );

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }} role="status" aria-label={tc('loading')}>
        <CircularProgress />
      </Box>
    );
  }

  // Map API InspectionDetail to the Inspection shape expected by UI components
  const inspection = inspectionData ? {
    ...inspectionData,
    status: inspectionData.status as InspectionStatus,
    workOrderId: inspectionData.bookingId,
    workOrderNumber: inspectionData.workOrderNumber ?? tWorkorders('noWorkOrderNumber'),
    vehicleId: inspectionData.vehicle.id ?? '',
    customerId: inspectionData.customer.id ?? '',
    mileage: inspectionData.vehicleMileage ?? 0,
    vehicle: {
      ...inspectionData.vehicle,
      id: inspectionData.vehicle.id ?? '',
      customerId: inspectionData.customer.id ?? '',
      registrationNumber: inspectionData.vehicle.registrationNumber ?? '',
      make: inspectionData.vehicle.make ?? '',
      model: inspectionData.vehicle.model ?? '',
      year: inspectionData.vehicle.year ?? 0,
    },
    customer: {
      ...inspectionData.customer,
      id: inspectionData.customer.id ?? '',
      firstName: inspectionData.customer.firstName ?? '',
      lastName: inspectionData.customer.lastName ?? '',
      email: '',
      phone: inspectionData.customer.phone ?? '',
    },
    mechanic: {
      id: inspectionData.mechanicId,
      firstName: inspectionData.mechanic.firstName ?? '',
      lastName: inspectionData.mechanic.lastName ?? '',
      email: '',
      phone: '',
      skills: [],
      isAvailable: true,
    },
    notes: inspectionData.internalNotes ?? undefined,
    recommendations: undefined,
    totalRecommendedCents: inspectionData.totalRecommendationsCents,
    completedAt: inspectionData.completedAt ?? undefined,
    sentAt: undefined,
    // Map items into categories for the inspection components
    categories: groupItemsIntoCategories(inspectionData),
  } satisfies Inspection : null;

  if (error) {
    return (
      <Box>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => { void navigate('/inspections'); }}
          sx={{ mb: 2 }}
        >
          {tc('back')}
        </Button>
        <Alert
          severity="error"
          action={
            <Button color="inherit" size="small" onClick={() => void refetch()}>
              {tc('errors.refresh')}
            </Button>
          }
        >
          {t('detail.notFound')}
        </Alert>
      </Box>
    );
  }

  if (!inspection) {
    return (
      <Box>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => { void navigate('/inspections'); }}
          sx={{ mb: 2 }}
        >
          {tc('back')}
        </Button>
        <Typography variant="h5">{t('detail.notFound')}</Typography>
      </Box>
    );
  }

  const inspectionStatus = inspection.status as InspectionStatus;
  const canComplete = canEdit && inspectionStatus === 'in_progress';
  const canSend = hasPermission('inspections.send_report') && inspectionStatus === 'completed';

  const handleCompleteInspection = () => {
    if (!id || !canComplete) return;
    void Promise.all(itemSaves.current.values()).then(() => {
      if (failedItemSaves.current.size > 0) throw new Error(t('detail.saveBeforeComplete'));
      completeInspection.mutate(
        { id },
        {
          onSuccess: () => notify.success(t('detail.inspectionComplete'), t('detail.inspectionMarkedComplete')),
          onError: () => notify.error(tc('error'), t('detail.completeFailed')),
        }
      );
    }).catch(() => notify.error(tc('error'), t('detail.saveBeforeComplete')));
  };

  const handleSendToCustomer = async () => {
    if (!id) return;

    try {
      // Generate a customer report first
      const report = await generateReport.mutateAsync({
        inspectionId: id,
        reportType: 'customer',
        language: 'fi',
      });
      setCurrentReportId(report.id);
      setSendDialogOpen(true);
    } catch {
      notify.error(tc('error'), t('detail.reportGenerationFailed'));
    }
  };

  const handleSendReport = async (params: {
    inspectionId: string;
    reportId: string;
    channel: 'whatsapp' | 'email';
    recipientPhone?: string;
    recipientEmail?: string;
    message?: string;
  }) => {
    await sendReport.mutateAsync(params);
    notify.success(t('detail.sent'), t('detail.reportSentVia', { channel: params.channel === 'whatsapp' ? 'WhatsApp' : t('detail.email') }));
  };

  const handlePrintReport = () => {
    generateInspectionReport({
      inspection,
      ...workshopInfo,
    });
  };

  // Work order items from the API
  const workOrderItems: WorkOrderItem[] = workOrderData?.items ?? [];

  const isLinking =
    linkRecommendation.isPending ||
    unlinkRecommendation.isPending ||
    createInspectionOffer.isPending;

  const offerFindings = (inspectionData?.recommendations ?? []).map(toOfferFinding);

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
        <Tooltip title={tc('back')}>
          <IconButton onClick={() => { void navigate('/inspections'); }} aria-label={tc('back')}>
            <ArrowBackIcon />
          </IconButton>
        </Tooltip>
        <Box sx={{ flex: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Typography variant="h4" fontWeight={600}>
              {t('inspection')} - {inspection.vehicle.registrationNumber}
            </Typography>
            <Chip
              label={statusConfig[inspectionStatus]?.label ?? inspectionStatus}
              color={statusConfig[inspectionStatus]?.color ?? 'default'}
            />
          </Box>
          <Typography variant="body2" color="text.secondary">
            {inspection.workOrderNumber} | {inspection.vehicle.make} {inspection.vehicle.model}
          </Typography>
        </Box>

        {/* Actions */}
        <Button
          startIcon={<SmartphoneIcon />}
          variant="outlined"
          onClick={() => { void navigate(`/inspections/${id}/mobile`); }}
          sx={{ display: { xs: 'none', md: 'inline-flex' } }}
        >
          {t('detail.mobile')}
        </Button>
        <Button startIcon={<PrintIcon />} variant="outlined" onClick={handlePrintReport}>
          {t('print')}
        </Button>
        {canEdit && (
          <Button
            startIcon={<EditIcon />}
            variant={isEditing ? 'contained' : 'outlined'}
            onClick={() => { setIsEditing(!isEditing); }}
          >
            {isEditing ? t('stopEditing') : t('edit')}
          </Button>
        )}
        {canComplete && (
          <Button
            startIcon={<CheckCircleIcon />}
            variant="contained"
            color="success"
            onClick={handleCompleteInspection}
          >
            {t('complete')}
          </Button>
        )}
        {canSend && (
          <Button
            startIcon={<SendIcon />}
            variant="contained"
            onClick={() => void handleSendToCustomer()}
            disabled={generateReport.isPending}
          >
            {generateReport.isPending ? t('detail.generatingReport') : t('sendToCustomer')}
          </Button>
        )}
      </Box>

      <Grid container spacing={3}>
        {/* Main Content - Categories */}
        <Grid size={{ xs: 12, lg: 8 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {inspection.categories.map((category) => (
              <InspectionCategoryComponent
                key={category.id}
                category={category}
                editable={editable}
                defaultExpanded={category.items.some(
                  (item) => item.condition === 'poor' || item.condition === 'critical'
                )}
                onConditionChange={handleConditionChange}
                onNotesChange={handleNotesChange}
                onMeasurementChange={handleMeasurementChange}
                onMeasurementValuesChange={handleMeasurementValuesChange}
                onAddRecommendation={async (itemId, recommendation) => {
                  if (!id || !editable) throw new Error(t('detail.saveFailed'));
                  await addRecommendation.mutateAsync({ inspectionId: id, inspectionItemId: itemId, ...recommendation });
                }}
                isAddingRecommendation={addRecommendation.isPending}
                vehicleMake={inspection.vehicle.make}
                vehicleModel={inspection.vehicle.model}
                onAddPhoto={handleAddPhoto}
                onDeletePhoto={handleDeletePhoto}
                isUploadingPhoto={addPhoto.isPending}
                isSavingItem={updateItemCondition.isPending}
              />
            ))}
          </Box>

          {/* Notes */}
          {inspection.notes && (
            <Box sx={{ mt: 3 }}>
              <Typography variant="h6" gutterBottom>
                {t('notes')}
              </Typography>
              <Typography variant="body2">{inspection.notes}</Typography>
            </Box>
          )}
        </Grid>

        {/* Sidebar - Summary */}
        <Grid size={{ xs: 12, lg: 4 }}>
          <InspectionSummary
            inspection={inspection}
            workOrderNumber={inspection.workOrderNumber}
            workOrderItems={workOrderItems}
            offerFindings={offerFindings}
            onLinkRecommendation={canUpdate ? handleLinkRecommendation : undefined}
            onCreateAndLinkRecommendation={canOffer ? handleCreateAndLinkRecommendation : undefined}
            onUnlinkRecommendation={canUpdate ? handleUnlinkRecommendation : undefined}
            onEditRecommendation={editable ? setEditingRecommendationId : undefined}
            onDeleteRecommendation={editable ? setDeletingRecommendationId : undefined}
            managedFindingIds={(inspectionData?.recommendations ?? []).filter((rec) => rec.offerId || rec.approvalRequestId).map((rec) => rec.id)}
            {...(canOffer ? { onAddToOffer: handleAddToOffer } : {})}
            isLinking={isLinking}
            isAddingToOffer={createInspectionOffer.isPending}
          />
          <InspectionReportDeliveries inspectionId={id} />
        </Grid>
      </Grid>

      {editingRecommendation && editableRecommendation(editingRecommendation.id) && (
        <AddRecommendationDialog
          open
          recommendation={editingRecommendation}
          item={{ id: editingItem?.id ?? '', name: editingItem?.itemName ?? t('summary.recommendations'), categoryId: editingItem?.categoryId ?? '', condition: editingItem?.condition ?? null }}
          onClose={() => setEditingRecommendationId(null)}
          onSave={async (changes) => {
            if (!id || !editableRecommendation(editingRecommendation.id)) throw new Error(t('detail.saveFailed'));
            await updateRecommendation.mutateAsync({ inspectionId: id, recId: editingRecommendation.id, ...changes });
          }}
          isLoading={updateRecommendation.isPending}
        />
      )}
      <ConfirmDialog
        open={!!deletingRecommendationId && !!editableRecommendation(deletingRecommendationId)}
        title={t('recommendation.delete')}
        message={t('recommendation.deleteConfirm')}
        confirmColor="error"
        loading={deleteRecommendation.isPending}
        onCancel={() => setDeletingRecommendationId(null)}
        onConfirm={() => {
          if (!id || !deletingRecommendationId || !editableRecommendation(deletingRecommendationId)) return;
          void deleteRecommendation.mutateAsync({ inspectionId: id, recId: deletingRecommendationId }).then(
            () => setDeletingRecommendationId(null),
            () => notify.error(tc('error'), t('recommendation.deleteFailed')),
          );
        }}
      />

      {/* Send Report Dialog */}
      {currentReportId && (
        <SendReportDialog
          open={sendDialogOpen}
          onClose={() => {
            setSendDialogOpen(false);
            setCurrentReportId(null);
          }}
          inspectionId={id ?? ''}
          reportId={currentReportId}
          customerPhone={inspection.customer?.phone ?? null}
          customerEmail={inspection.customer?.email ?? null}
          customerName={`${inspection.customer?.firstName ?? ''} ${inspection.customer?.lastName ?? ''}`.trim()}
          vehicleRegNumber={inspection.vehicle.registrationNumber}
          onSend={handleSendReport}
          isLoading={sendReport.isPending}
        />
      )}
    </Box>
  );
}
